import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/work", label: "Work", id: "work" },
  { to: "/services", label: "Services", id: "services" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-4 inset-x-0 z-50 flex justify-center px-4">
      <nav
        data-testid="main-nav"
        className="relative flex w-full max-w-3xl items-center justify-between gap-4 rounded-full border border-[#1E2335] bg-[#07080C]/85 px-4 py-2 backdrop-blur-xl"
      >
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2.5" onClick={() => setOpen(false)}>
          <img src="/images/fredline-mark.png" alt="Fredline Studios F logo" className="h-8 w-8 object-contain" />
          <span className="font-display text-sm font-bold tracking-[0.2em] text-slate-100 hidden sm:block">
            Fredline Studios
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-[13px] font-medium tracking-wide transition-colors duration-200 ${
                  isActive ? "text-cyan-300" : "text-slate-400 hover:text-slate-100"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-active-line"
                      className="absolute inset-x-4 -bottom-px h-px bg-cyan-400"
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-4 py-2 text-[12px] font-semibold tracking-wider text-cyan-300 uppercase transition-all duration-200 hover:bg-cyan-400 hover:text-[#07080C] hover:shadow-[0_0_28px_rgba(0,240,255,0.4)]"
          >
            Start a Project
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
          <button
            data-testid="nav-mobile-menu-button"
            className="md:hidden p-2 text-slate-200"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              data-testid="nav-mobile-menu"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="absolute top-full left-0 right-0 mt-3 rounded-2xl border border-[#1E2335] bg-[#0D0E15]/95 backdrop-blur-xl p-3 md:hidden"
            >
              {LINKS.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => setOpen(false)}
                  className={() =>
                    `block px-4 py-3 text-sm font-medium tracking-wide rounded-lg transition-colors duration-200 ${
                      location.pathname === l.to ? "text-cyan-300 bg-cyan-400/10" : "text-slate-300 hover:bg-white/5"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}
